import { useApolloClient } from '@apollo/react-hooks';
import { LIST_REWARD_SET, RewardSet } from '@bowtie-web/common';
import { compact, flatten } from 'lodash';
import { useMemo } from 'react';
import { useAsyncFn, useDeepCompareEffect } from 'react-use';
import {
  getCouponSelectionToProductIdMapping,
  getEffectiveDefaultCouponMappings,
  getUniqueTypeDefaultCouponMappings,
} from '../helpers';
import useListDefaultCouponMapping from './useListDefaultCouponMapping';

const useCouponOptions = (
  productIds: string[],
  defaultCouponTypes: string[],
) => {
  const client = useApolloClient();

  const [
    defaultCouponMappingBase,
    { loading: isListDefaultCouponMappingLoading },
  ] = useListDefaultCouponMapping(
    {
      typeCodes: defaultCouponTypes,
      ordering: '-effective_from',
    },
    { skip: defaultCouponTypes.length === 0 || productIds.length === 0 },
  );

  const [defaultCouponMapping, couponCodes] = useMemo(() => {
    const mapping = getEffectiveDefaultCouponMappings(
      getUniqueTypeDefaultCouponMappings(defaultCouponMappingBase),
    );

    const codes = mapping.map(({ coupon_code }) => coupon_code);

    return [mapping, codes];
  }, [defaultCouponMappingBase]);

  const [
    { value: rewardSets, loading: isListRewardSetLoading },
    listRewardSet,
  ] = useAsyncFn(async (couponCodes: string[]) => {
    const results = await Promise.all(
      couponCodes.map(async couponCode => {
        const { data } = await client.query<{ rewardSets: RewardSet[] }>({
          query: LIST_REWARD_SET,
          variables: { coupon_code: couponCode },
        });

        return data.rewardSets && data.rewardSets.length > 0
          ? data.rewardSets.map(set => ({ ...set, coupon_code: couponCode }))
          : null;
      }),
    );

    return flatten(compact(results));
  });

  useDeepCompareEffect(() => {
    if (couponCodes.length > 0) {
      listRewardSet(couponCodes);
    }
  }, [couponCodes]);

  return useMemo(
    () => [
      getCouponSelectionToProductIdMapping(
        productIds,
        defaultCouponMapping,
        rewardSets ?? [],
      ),
      {
        loading: isListDefaultCouponMappingLoading || isListRewardSetLoading,
      },
    ],
    [
      productIds,
      defaultCouponMapping,
      rewardSets,
      isListDefaultCouponMappingLoading,
      isListRewardSetLoading,
    ],
  );
};

export default useCouponOptions;
